import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { Messages } from '../../../codegen/Localize';
import { openReactConfirmationDialog } from '../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { IApiToken } from '../../../httpClient/serverApi/types/IApiToken';
import {getSessionManagementServiceInstance} from '../../../sessionManagement/SessionManagementService';


export interface ScimSettingsProps {
}

export interface ScimSettingsState {
    scimProvisioningEnabled: boolean;
    baseUrl: string;
    secret: string;
}

export class ScimSettings extends React.Component<ScimSettingsProps, ScimSettingsState> {

    private SCIM_CLIENT_API_TOKEN = 'SCIM_CLIENT_API_TOKEN';

    constructor(props: ScimSettingsProps) {
        super(props);
        this.state = {
            scimProvisioningEnabled: false,
            baseUrl: '',
            secret: null
        };
    }

    public componentDidMount() {
        this.initScimApiToken();
    }

    private selectAll(event: any): void {
        event.target.select();
    }

    private openSCIMHelp(): void {
        window.open('https://onlinehelp.tableau.com/current/online/en-us/scim_config_online.htm', '_blank');
    }

    private getServerApiTokens = () => {
        return getSessionManagementServiceInstance().getApiTokens();
    };

    private getServerSessionSiteLuid = () => {
        return getSessionManagementServiceInstance().sessionSiteLuid();
    };

    private serverDeleteApiToken = (tokenId: string) => {
        return getSessionManagementServiceInstance().deleteApiToken(tokenId);
    };

    private serverCreateApiToken = (clientName: string) => {
        return getSessionManagementServiceInstance().createApiToken(clientName);
    };

    private initScimApiToken() {
        this.getServerApiTokens().then((allApiTokens: IApiToken[]) => {
            const scimApiTokens: IApiToken[] = allApiTokens.
            filter(token => token.clientName === this.SCIM_CLIENT_API_TOKEN);

            this.setScimProvisioning(scimApiTokens.length > 0);
            this.setBaseUrl(this.getBaseUrl(this.state.scimProvisioningEnabled));
        });
    }

    private getBaseUrl(scimEnabled: boolean): string {
        if (!scimEnabled) {
            return 'https://';
          }
        const podName = window.location.hostname === 'localhost' ?
            'local-pod-fake' : window.location.hostname.split('.')[0];
        return 'https://scim.online.tableau.com/pods/' + podName +
            '/sites/' + this.getServerSessionSiteLuid() + '/scim/v2';
    }

    toggleScimProvisioning = (checked: boolean) => {
        var result;
        if (this.state.scimProvisioningEnabled) {
            result = this.deleteScimApiTokens();
        } else {
            result = this.createScimApiToken();
        }
        result.then(() => {
            this.setScimProvisioning(checked);
            this.setBaseUrl(this.getBaseUrl(this.state.scimProvisioningEnabled));
        }).catch(err => err);
    };

    alertAndDeleteOldTokensAndGenerateNewToken<TResult>(): void {
        openReactConfirmationDialog<void, TResult>({
            title: Messages.siteAuthenticationScimSecretGenerate(),
            message: Messages.siteAuthenticationScimSecretGenerateConfirmBody(),
            paramsForAction: undefined,
            action: () => {
              return this.deleteScimApiTokens().then(() => this.createScimApiToken());
            },
            actionName: Messages.siteAuthenticationScimSecretGenerate(),
            isActionDelete: false,
            dialogWidth: TabDialogWidth.width375px
        });
    }

    deleteScimApiTokens(): Promise<any> {
        return this.notifyOnFail(this.getServerApiTokens().
        then((allApiTokens: IApiToken[]) => {
            const scimApiTokens: IApiToken[] = allApiTokens.
            filter(token => token.clientName === this.SCIM_CLIENT_API_TOKEN);

            scimApiTokens.map(token => this.serverDeleteApiToken(token.id));
        }).then(() => this.setSecret(null)));
    }

    createScimApiToken(): Promise<any> {
        return this.notifyOnFail(this.serverCreateApiToken(this.SCIM_CLIENT_API_TOKEN).
        then((newApiToken: IApiToken) => this.setSecret(newApiToken.secret)));
    }

    private setSecret(tokenSecret: string): void {
        return this.setState({secret: tokenSecret});
    }

    private setScimProvisioning(enabled: boolean): void {
        return this.setState({scimProvisioningEnabled: enabled});
    }

    private setBaseUrl(url: string): void {
        return this.setState({baseUrl: url});
    }

    private notifyOnFail<TResult>(result: Promise<TResult>): Promise<TResult> {
        result.catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError()});
        });
        return result;
    }

    private renderScimSettings(): JSX.Element {
        return (
            <>
            <div className="tb-settings-group">
                <div className="tb-settings-group-name">
                    {Messages.siteAuthenticationScim()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-control">
                    {Messages.siteAuthenticationScimDesc()}
                    &nbsp;
                    <TextLinkWidget onClick={this.openSCIMHelp} testId={'scim-learn-more-link'}>
                        {Messages.scimLearnMore()}
                    </TextLinkWidget>
                    </div>
                    <CheckBoxWithLabelWidget
                    label={Messages.siteAuthenticationScimEnable()}
                    checked={this.state.scimProvisioningEnabled}
                    testId={'scim-checkbox'}
                    disabled={false}
                    removeLeftMargin={true}
                    handleChange={this.toggleScimProvisioning}/>
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            {Messages.siteAuthenticationScimBaseUrl()}
                        </div>
                        <input className="tb-readonly-field" type="text" value={this.state.baseUrl} disabled={!this.state.scimProvisioningEnabled} readOnly></input>
                    </div>
                    <div className="tb-auth-options-list">
                        <table>
                            <tbody>
                            <tr>
                                <td>
                                    <ButtonWidget
                                    buttonType={ButtonType.Outline}
                                    onClick={this.alertAndDeleteOldTokensAndGenerateNewToken.bind(this)}
                                    testId={'scim-create-apitoken'}
                                    disabled={!this.state.scimProvisioningEnabled}>
                                        {Messages.siteAuthenticationScimSecretGenerate()}
                                    </ButtonWidget>
                                </td>
                                {this.state.secret ?
                                <td className="tb-auth-options-list-item">
                                <input className="tb-readonly-field" type="text" value={this.state.secret} onClick={this.selectAll} readOnly></input>
                                &nbsp;
                                 </td>
                                : null}
                            </tr>
                            <tr>
                                <td>&nbsp;</td>
                                {this.state.secret ?
                                <td className="tb-auth-options-list-item">
                                <div className="tb-settings-msg">
                                    {Messages.siteAuthenticationScimSecretMsg()}
                                </div>
                                </td>
                                : null}
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            </>
        );
    }

    public render(): JSX.Element {
        return FeatureFlags.scimApiTokenEnabled ?
            this.renderScimSettings()
        : null;
    }

}
